// Application constants
export const APP_CONFIG = {
  NAME: 'Sage Locum',
  VERSION: '1.0.0',
  DESCRIPTION: 'Sage Locum Frontend Dashboard'
};

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login/',
    REFRESH: '/auth/refresh/',
    VERIFY: '/auth/verify/',
  },
  USERS: {
    SIGNUP: '/users/signup/',
    VERIFY_EMAIL: '/users/verify_email/',
    RESEND_VERIFICATION: '/users/resend_verification_code/',
    ME: '/users/me/',
    UPDATE_ME: '/users/update_me/',
    LOGOUT: '/users/logout/',
    BY_ID: (id) => `/users/${id}/`,
    VERIFY_USER: (id) => `/users/${id}/verify_user/`,
    VERIFY_DOCUMENT: (documentId) => `/users/documents/${documentId}/verify/`,
    // Document APIs
    REQUIRED_DOCUMENTS: '/users/required_documents/',
    UPLOAD_DOCUMENT: '/users/upload_document/',
    MY_DOCUMENTS: '/users/my_documents/',
  },
  USER_ROLES: {
    LIST: '/user-roles/',
    BY_ID: (id) => `/user-roles/${id}/`,
  },
  JOB_ROLES: {
    LIST: '/job-roles/',
    BY_ID: (id) => `/job-roles/${id}/`,
    DOCUMENTS: (id) => `/job-roles/${id}/documents/`,
  },
  DOCUMENT_TYPES: {
    LIST: '/document-types/',
    BY_ID: (id) => `/document-types/${id}/`,
    JOB_ROLES: (id) => `/document-types/${id}/job_roles/`,
  },
  JOB_ROLE_DOCUMENTS: {
    LIST: '/job-role-documents/',
    BY_ID: (id) => `/job-role-documents/${id}/`,
  },
  JOBS: {
    LIST: '/jobs/',
    BY_ID: (id) => `/jobs/${id}/`,
    POST: '/jobs/',
    MATCHES: '/jobs/matches/',
    ACCEPT_MATCH: (matchId) => `/jobs/matches/${matchId}/accept/`,
    REJECT_MATCH: (matchId) => `/jobs/matches/${matchId}/reject/`,
  },
  JOB_POSTINGS: {
    LIST: '/job-postings/',
    BY_ID: (id) => `/job-postings/${id}/`,
  },
  JOB_MATCHES: {
    LIST: '/job-matches/',
    BY_ID: (id) => `/job-matches/${id}/`,
    ACCEPT: (id) => `/job-matches/${id}/accept/`,
    REJECT: (id) => `/job-matches/${id}/reject/`,
  },
  CALENDAR: {
    EVENTS: '/calendar/events/',
    AVAILABILITY: '/calendar/availability/',
    MARK_AVAILABILITY: '/calendar/availability/',
  },
  CONTRACTS: {
    ACTIVE: '/contracts/active/',
    ACCEPT: '/contracts/accept/',
    LEGACY_CURRENT: '/contract/current/',
  },
};

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  SIGNUP: '/signup',
  REGISTER: '/register',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  CONTRACT: '/contract',
  PENDING_VERIFICATION: '/pending-verification',
  NOT_FOUND: '/404'
};

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
  CONTRACT_ACCEPTANCE: 'contractAcceptance',
  ACTIVE_CONTRACT: 'activeContract',
  SIGNUP_PASSWORD: 'signupPassword',
  THEME: 'theme',
  LANGUAGE: 'language'
};

// React Query keys
export const QUERY_KEYS = {
  AUTH: {
    CURRENT_USER: ['auth', 'currentUser'],
  },
  USERS: {
    ME: ['users', 'me'],
    BY_ID: (id) => ['users', id],
    REQUIRED_DOCUMENTS: ['users', 'required_documents'],
    MY_DOCUMENTS: ['users', 'my_documents'],
  },
  USER_ROLES: {
    LIST: ['userRoles', 'list'],
    BY_ID: (id) => ['userRoles', id],
  },
  JOB_ROLES: {
    LIST: ['jobRoles', 'list'],
    BY_ID: (id) => ['jobRoles', id],
  },
  JOBS: {
    LIST: ['jobs', 'list'],
    BY_ID: (id) => ['jobs', id],
    MATCHES: ['jobs', 'matches'],
    MATCH_BY_ID: (id) => ['jobs', 'matches', id],
  },
  JOB_POSTINGS: {
    LIST: ['job-postings', 'list'],
    BY_ID: (id) => ['job-postings', id],
  },
  CALENDAR: {
    EVENTS: ['calendar', 'events'],
    AVAILABILITY: ['calendar', 'availability'],
  },
  ADMIN_DASHBOARD: ['admin', 'dashboard'],
  ADMIN_EMPLOYEES: ['admin', 'employees'],
  ADMIN_EMPLOYEE_DETAILS: (id) => ['admin', 'employees', id],
  ADMIN_EMPLOYERS: ['admin', 'employers'],
  ADMIN_EMPLOYER_DETAILS: (id) => ['admin', 'employers', id],
  ADMIN_JOBS: ['admin', 'jobs'],
  ADMIN_PENDING_JOBS: ['admin', 'jobs', 'pending'],
  ADMIN_COMPLETED_JOBS: ['admin', 'jobs', 'completed'],
  ADMIN_JOB_DETAILS: (id) => ['admin', 'jobs', id],
  ADMIN_JOB_ROLES: ['admin', 'job-roles'],
  ADMIN_DOCUMENTS: ['admin', 'documents'],
  PAYMENTS: ['payments', 'list'],
  PAYMENT_DETAILS: (id) => ['payments', id],
  PENDING_PAYMENTS: ['payments', 'pending'],
  MY_PAYMENTS: ['payments', 'my'],
};

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500
};

export const VALIDATION_RULES = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD_MIN_LENGTH: 8,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 50
};

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100
};

export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  API: 'YYYY-MM-DD',
  DATETIME: 'MMM DD, YYYY HH:mm'
};

// External links and resources
export const EXTERNAL_LINKS = {
  GDC_SEARCH_REGISTER: 'https://olr.gdc-uk.org/SearchRegister',
  GDC_SEARCH_BY_NUMBER: (registrationNumber) => 
    `https://olr.gdc-uk.org/SearchRegister/SearchResult?RegistrationNumber=${registrationNumber}`
};

// Admin resources (for future admin view implementation)
export const ADMIN_RESOURCES = {
  GDC_SEARCH_REGISTER: EXTERNAL_LINKS.GDC_SEARCH_REGISTER,
  GDC_SEARCH_BY_NUMBER: EXTERNAL_LINKS.GDC_SEARCH_BY_NUMBER
};

// Supported job roles in the system
export const SUPPORTED_JOB_ROLES = {
  NURSE: 'Nurse',
  HYGIENIST: 'Hygienist',
  DENTIST: 'Dentist',
  THERAPIST: 'Therapist',
  RECEPTIONIST: 'Receptionist'
};

// List of all supported roles (for dropdowns, validation, etc.)
export const JOB_ROLES_LIST = [
  SUPPORTED_JOB_ROLES.NURSE,
  SUPPORTED_JOB_ROLES.HYGIENIST,
  SUPPORTED_JOB_ROLES.DENTIST,
  SUPPORTED_JOB_ROLES.THERAPIST,
  SUPPORTED_JOB_ROLES.RECEPTIONIST
];

// Roles that require GDC registration
export const GDC_REQUIRED_ROLES = [
  SUPPORTED_JOB_ROLES.DENTIST,
  SUPPORTED_JOB_ROLES.HYGIENIST,
  SUPPORTED_JOB_ROLES.THERAPIST,
  SUPPORTED_JOB_ROLES.NURSE
];
